import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/api';

// Async thunks
export const fetchActivityLogs = createAsyncThunk(
  'activityLogs/fetchActivityLogs',
  async (params = {}) => {
    const response = await api.get('/activity-logs', { params });
    return response.data;
  }
);

export const fetchActivityLog = createAsyncThunk(
  'activityLogs/fetchActivityLog',
  async (id) => {
    const response = await api.get(`/activity-logs/${id}`);
    return response.data;
  }
);

export const createActivityLog = createAsyncThunk(
  'activityLogs/createActivityLog',
  async (logData) => {
    const response = await api.post('/activity-logs', logData);
    return response.data;
  }
);

export const updateActivityLog = createAsyncThunk(
  'activityLogs/updateActivityLog',
  async ({ id, data }) => {
    const response = await api.put(`/activity-logs/${id}`, data);
    return response.data;
  }
);

export const fetchActivityStats = createAsyncThunk(
  'activityLogs/fetchActivityStats',
  async (params = {}) => {
    const response = await api.get('/activity-logs/stats', { params });
    return response.data;
  }
);

// Fetch batch summaries
export const fetchBatchSummaries = createAsyncThunk(
  'activityLogs/fetchBatchSummaries',
  async (params = {}) => {
    const response = await api.get('/activity-logs/batches', { params });
    return response.data;
  }
);

// Fetch emails in a batch
export const fetchBatchEmails = createAsyncThunk(
  'activityLogs/fetchBatchEmails',
  async ({ groupId, params = {} }) => {
    const response = await api.get(`/activity-logs/batch/${groupId}`, { params });
    return response.data;
  }
);

// Fetch batch status for real-time updates
export const fetchBatchStatus = createAsyncThunk(
  'activityLogs/fetchBatchStatus',
  async (groupId) => {
    const response = await api.get(`/activity-logs/batch/${groupId}/status`);
    return response.data;
  }
);

const activityLogSlice = createSlice({
  name: 'activityLogs',
  initialState: {
    items: [],
    currentLog: null,
    stats: null,
    batches: [],
    batchEmails: [],
    currentBatchStatus: null,
    loading: false,
    batchLoading: false,
    error: null,
    total: 0,
    page: 1,
    pages: 1,
    batchTotal: 0,
    batchPage: 1,
    batchPages: 1,
    emailTotal: 0,
    emailPage: 1,
    emailPages: 1
  },
  reducers: {
    clearCurrentLog: (state) => {
      state.currentLog = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearStats: (state) => {
      state.stats = null;
    },
    clearBatchEmails: (state) => {
      state.batchEmails = [];
      state.emailTotal = 0;
      state.emailPage = 1;
      state.emailPages = 1;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch activity logs
      .addCase(fetchActivityLogs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchActivityLogs.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.logs;
        state.total = action.payload.total;
        state.page = action.payload.page;
        state.pages = action.payload.pages;
      })
      .addCase(fetchActivityLogs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      
      // Fetch single activity log
      .addCase(fetchActivityLog.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchActivityLog.fulfilled, (state, action) => {
        state.loading = false;
        state.currentLog = action.payload;
      })
      .addCase(fetchActivityLog.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      
      // Create activity log
      .addCase(createActivityLog.fulfilled, (state, action) => {
        state.items.unshift(action.payload);
        state.total += 1;
      })
      
      // Update activity log
      .addCase(updateActivityLog.fulfilled, (state, action) => {
        const index = state.items.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
        if (state.currentLog?.id === action.payload.id) {
          state.currentLog = action.payload;
        }
      })
      
      // Fetch activity stats
      .addCase(fetchActivityStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchActivityStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload;
      })
      .addCase(fetchActivityStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      
      // Fetch batch summaries
      .addCase(fetchBatchSummaries.pending, (state) => {
        state.batchLoading = true;
        state.error = null;
      })
      .addCase(fetchBatchSummaries.fulfilled, (state, action) => {
        state.batchLoading = false;
        state.batches = action.payload.batches;
        state.batchTotal = action.payload.total;
        state.batchPage = action.payload.page;
        state.batchPages = action.payload.pages;
      })
      .addCase(fetchBatchSummaries.rejected, (state, action) => {
        state.batchLoading = false;
        state.error = action.error.message;
      })
      
      // Fetch batch emails
      .addCase(fetchBatchEmails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBatchEmails.fulfilled, (state, action) => {
        state.loading = false;
        state.batchEmails = action.payload.emails;
        state.emailTotal = action.payload.total;
        state.emailPage = action.payload.page;
        state.emailPages = action.payload.pages;
      })
      .addCase(fetchBatchEmails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      
      // Fetch batch status
      .addCase(fetchBatchStatus.fulfilled, (state, action) => {
        state.currentBatchStatus = action.payload;
      });
  }
});

export const { clearCurrentLog, clearError, clearStats, clearBatchEmails } = activityLogSlice.actions;
export default activityLogSlice.reducer;